import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, LogOut, Settings as SettingsIcon, User as UserIcon, Shield, MessageCircle, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { t } from "@/lib/i18n";
import { initials } from "@/lib/format";
import NotificationsBell from "@/components/NotificationsBell";

export default function Header() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState("");
  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  const isAdminArea = loc.pathname.startsWith("/admin");

  return (
    <header className="motif-header bg-primary text-primary-foreground sticky top-0 z-30 shadow-md">
      <div className="max-w-6xl mx-auto flex items-center gap-4 px-4 sm:px-6 py-3 relative">
        {/* Brand */}
        <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="brand-link">
          <div className="w-9 h-9 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center">
            <span className="font-eng font-bold text-lg tracking-tighter">PN</span>
          </div>
          <div className="leading-tight">
            <div className="font-eng font-bold text-xl tracking-tight">PakNet</div>
            <div className="urdu-naskh text-[11px] opacity-80 -mt-0.5">{t.tagline}</div>
          </div>
        </Link>

        {/* Search (hidden on very small) */}
        <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-md relative" data-testid="search-form">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 opacity-60" size={16} />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t.searchPlaceholder}
            className="pr-9 bg-white/10 border-white/20 text-primary-foreground placeholder:text-primary-foreground/60 focus-visible:ring-white/40 focus-visible:ring-offset-0"
            data-testid="search-input"
          />
        </form>

        <nav className="mr-auto flex items-center gap-1.5">
          {user && <NotificationsBell />}
          {user && (
            <Link
              to="/messages"
              className="p-2 rounded-md hover:bg-white/10 text-primary-foreground transition-colors"
              title="نجی پیغامات"
              data-testid="header-messages"
            >
              <Mail size={18} />
            </Link>
          )}
          {/* User area */}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-white/10 transition-colors"
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="h-8 w-8 border border-white/30">
                    <AvatarImage src={user.avatar_url || undefined} />
                    <AvatarFallback className="bg-white/15 text-white text-xs font-eng font-semibold">
                      {initials(user.username)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline font-eng text-sm">{user.username}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52">
                <DropdownMenuLabel className="font-eng">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav(`/u/${user.username}`)} data-testid="menu-profile">
                  <UserIcon size={14} /> <span>{t.profile}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/settings")} data-testid="menu-settings">
                  <SettingsIcon size={14} /> <span>{t.settings}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/messages")} data-testid="menu-messages">
                  <Mail size={14} /> <span>نجی پیغامات</span>
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">
                    <Shield size={14} /> <span className="font-eng">{t.admin} Panel</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="menu-logout">
                  <LogOut size={14} /> <span>{t.logout}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm" className="text-primary-foreground hover:bg-white/10 hover:text-primary-foreground">
                <Link to="/login" data-testid="header-login-link">{t.login}</Link>
              </Button>
              <Button asChild size="sm" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold">
                <Link to="/register" data-testid="header-register-link">{t.register}</Link>
              </Button>
            </>
          )}
        </nav>
      </div>
      {isAdminArea && (
        <div className="border-t border-white/15 bg-primary/95 font-eng text-xs" dir="ltr">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-1.5 flex items-center gap-4">
            <Shield size={12} />
            <span className="opacity-80">Admin Control Panel — English mode</span>
          </div>
        </div>
      )}
    </header>
  );
}
